import { useEffect, useState } from "react";
import LoadingCircle from "../utility/LoadingCircle";
import ReviewPhotoPreview from "./ReviewPhotoPreview";
import ReviewSummary from "./ReviewSummary";

const VERDICT_STYLE = {
	PASS: "bg-green-100 text-green-800",
	FAIL: "bg-red-100 text-red-800",
	UNABLE_TO_VERIFY: "bg-amber-100 text-amber-900",
};

const VERDICT_EDGE = {
	PASS: "bg-green-600",
	FAIL: "bg-red-600",
	UNABLE_TO_VERIFY: "bg-amber-500",
};

const formatReviewDate = (value) => {
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return "";
	return parsed.toLocaleString();
};

const sameServerModified = (savedValue, currentValue) => {
	const savedDate = new Date(savedValue);
	const currentDate = new Date(currentValue);
	if (Number.isNaN(savedDate.getTime()) || Number.isNaN(currentDate.getTime())) return true;
	return Math.floor(savedDate.getTime() / 1000) === Math.floor(currentDate.getTime() / 1000);
};

const VERDICT_LABEL = {
	PASS: "Pass",
	FAIL: "Fail",
	UNABLE_TO_VERIFY: "Unable to verify",
};

const overallVerdict = (review) => {
	const verdicts = (review.photos || []).map((photo) => photo.verdict);
	if (verdicts.includes("FAIL")) return "FAIL";
	if (verdicts.includes("UNABLE_TO_VERIFY")) return "UNABLE_TO_VERIFY";
	if (verdicts.includes("PASS")) return "PASS";
	return "";
};

const passedPhotoCount = (review) => {
	const photos = review.photos || [];
	const passedCount = photos.filter((photo) => photo.verdict === "PASS").length;
	return `${passedCount}/${photos.length}`;
};

const AiReviewHistoryModal = ({ api, token, projectId, fieldId, onClose }) => {
	const [loading, setLoading] = useState(true);
	const [errorMessage, setErrorMessage] = useState("");
	const [reviewHistory, setReviewHistory] = useState([]);
	const [historyPage, setHistoryPage] = useState(1);
	const [reviewTotalCount, setReviewTotalCount] = useState(0);
	const [selectedReviewId, setSelectedReviewId] = useState(null);
	const [historyThumbnails, setHistoryThumbnails] = useState({});

	useEffect(() => {
		let cancelled = false;
		const loadReviewHistory = async () => {
			if (!api || !token || !projectId || !fieldId) {
				setErrorMessage("Unable to load review history.");
				setLoading(false);
				return;
			}
			try {
				const historyResponse = await fetch(`${api}/ai/reviews?projectId=${projectId}&fieldId=${fieldId}&page=${historyPage}`, {
					headers: {
						Accept: "application/json",
						Authorization: "Bearer " + token,
					},
				});
				const historyData = await historyResponse.json().catch(() => ({}));
				if (!historyResponse.ok) {
					throw new Error(historyData.msg || "Unable to load review history.");
				}
				if (!cancelled) {
					setReviewHistory(historyData.reviews || []);
					setReviewTotalCount(Number(historyData.total) || 0);
				}
			} catch (error) {
				if (!cancelled) setErrorMessage(error.message || "Unable to load review history.");
			} finally {
				if (!cancelled) setLoading(false);
			}
		};
		loadReviewHistory();
		return () => {
			cancelled = true;
		};
	}, [api, token, projectId, fieldId, historyPage]);

	useEffect(() => {
		let cancelled = false;
		const selectedReview = reviewHistory.find((review) => review.id === selectedReviewId);
		const photoPaths = [...new Set((selectedReview?.photos || []).map((photo) => photo.path).filter(Boolean))];
		const loadHistoryThumbnails = async () => {
			if (!api || !token || photoPaths.length === 0) return;
			try {
				const thumbnailResponse = await fetch(api + "/project/dropbox/thumbnails", {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
						Authorization: "Bearer " + token,
					},
					body: JSON.stringify({ entries: photoPaths }),
				});
				const thumbnailData = await thumbnailResponse.json().catch(() => ({}));
				const entries = thumbnailData.thumbnail?.entries || [];
				const nextThumbnails = {};
				photoPaths.forEach((photoPath, photoIndex) => {
					const entry = entries[photoIndex];
					if (!thumbnailResponse.ok || !entry || entry[".tag"] === "failure" || !entry.thumbnail) {
						nextThumbnails[photoPath] = { missing: true };
						return;
					}
					nextThumbnails[photoPath] = {
						missing: false,
						thumbnailValue: "data:image/jpeg;base64," + entry.thumbnail,
						serverModified: entry.metadata?.server_modified || null,
					};
				});
				if (!cancelled) setHistoryThumbnails(nextThumbnails);
			} catch (error) {
				if (!cancelled) {
					const missingThumbnails = {};
					photoPaths.forEach((photoPath) => {
						missingThumbnails[photoPath] = { missing: true };
					});
					setHistoryThumbnails(missingThumbnails);
				}
			}
		};
		loadHistoryThumbnails();
		return () => {
			cancelled = true;
		};
	}, [api, token, reviewHistory, selectedReviewId]);

	const selectedReview = reviewHistory.find((review) => review.id === selectedReviewId);

	return (
		<div className="fixed top-0 left-0 z-50 flex h-full w-full items-center justify-center bg-black/50 p-4">
			<div className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white">
				<div className="flex items-center justify-between gap-3 border-b border-stone-200 p-4">
					<p className="text-xl font-bold">AI review history</p>
					<button type="button" className="rounded bg-gray-500 px-3 py-1 text-white" onClick={selectedReview ? () => setSelectedReviewId(null) : onClose}>
						{selectedReview ? "Back" : "Close"}
					</button>
				</div>
				<div className="overflow-y-auto p-4">
				{loading ? (
					<div className="mt-8 flex flex-col items-center gap-3 pb-6">
						<LoadingCircle />
						<p className="text-sm text-stone-600">Loading review history…</p>
					</div>
				) : null}
				{errorMessage ? (
					<div className="mt-4 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800">
						<p className="whitespace-pre-wrap">{errorMessage}</p>
					</div>
				) : null}
				{!loading && !errorMessage && reviewHistory.length === 0 ? (
					<p className="mt-4 text-sm text-stone-600">No reviews have been saved for this field yet.</p>
				) : null}
				{!loading && !selectedReview && reviewHistory.length > 0 ? (
					<ul className="mt-4 flex flex-col gap-2">
						{reviewHistory.map((review) => {
							const verdict = overallVerdict(review);
							return (
								<li key={review.id}>
									<button
										type="button"
										className="relative flex w-full items-center justify-between gap-3 overflow-hidden rounded-lg border border-stone-200 px-3 py-3 pr-5 text-left hover:bg-stone-50"
										onClick={() => setSelectedReviewId(review.id)}>
										<span className={`absolute inset-y-0 right-0 w-2 ${VERDICT_EDGE[verdict] || "bg-stone-300"}`} />
										<span className="min-w-0">
											<span className="block text-sm font-semibold text-stone-800">Requested by {review.username || "Unknown"}</span>
											<span className="block text-sm text-stone-600">{formatReviewDate(review.reviewedOn)}</span>
										</span>
										<span className={`rounded px-2 py-0.5 text-xs font-semibold ${VERDICT_STYLE[verdict] || "bg-stone-100 text-stone-700"}`}>
											{VERDICT_LABEL[verdict] || "—"} {passedPhotoCount(review)}
										</span>
									</button>
								</li>
							);
						})}
					</ul>
				) : null}
				{!loading && !selectedReview && reviewTotalCount > 5 ? (
					<div className="mt-3 flex items-center justify-between gap-3">
						<button
							type="button"
							disabled={historyPage <= 1}
							onClick={() => setHistoryPage((currentPage) => currentPage - 1)}
							className="rounded bg-blue-600 px-3 py-1 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">
							Previous
						</button>
						<p className="text-sm text-stone-600">
							{historyPage} of {Math.ceil(reviewTotalCount / 5)}
						</p>
						<button
							type="button"
							disabled={historyPage * 5 >= reviewTotalCount}
							onClick={() => setHistoryPage((currentPage) => currentPage + 1)}
							className="rounded bg-blue-600 px-3 py-1 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">
							Next
						</button>
					</div>
				) : null}
				{selectedReview ? (
					<div className="flex flex-col gap-3">
						<ReviewSummary
							verdict={overallVerdict(selectedReview)}
							passedCount={(selectedReview.photos || []).filter((photo) => photo.verdict === "PASS").length}
							totalCount={(selectedReview.photos || []).length}
							dateLabel={formatReviewDate(selectedReview.reviewedOn)}
							requestedBy={selectedReview.username}
						/>
						{(selectedReview.photos || []).map((photo, photoIndex) => {
							const currentThumbnail = historyThumbnails[photo.path];
							const photoMissing = currentThumbnail?.missing;
							const photoChanged =
								!photoMissing &&
								photo.serverModified &&
								currentThumbnail?.serverModified &&
								!sameServerModified(photo.serverModified, currentThumbnail.serverModified);
							return (
								<div key={`${selectedReview.id}-${photo.path}-${photoIndex}`} className="relative flex items-start gap-3 overflow-hidden rounded-md border border-stone-200 p-3 pr-5">
									<span className={`absolute inset-y-0 right-0 w-2 ${VERDICT_EDGE[photo.verdict] || "bg-stone-300"}`} />
									<ReviewPhotoPreview
										thumbnailValue={currentThumbnail?.thumbnailValue}
										missing={photoMissing}
										loading={!photoMissing && !currentThumbnail?.thumbnailValue}
									/>
									<div className="min-w-0 flex-1">
										{photoChanged ? <p className="mb-2 text-sm font-semibold text-amber-800">Changed since this review</p> : null}
										{photoMissing ? <p className="mb-2 text-sm font-semibold text-stone-500">Deleted since</p> : null}
										<div className="flex flex-wrap items-center gap-2">
											<span className={`rounded px-2 py-0.5 text-xs font-semibold ${VERDICT_STYLE[photo.verdict] || "bg-stone-100 text-stone-700"}`}>
												{VERDICT_LABEL[photo.verdict] || photo.verdict || "—"}
											</span>
											{photo.serverModified ? <span className="text-xs text-stone-500">Photo modified {formatReviewDate(photo.serverModified)}</span> : null}
										</div>
										{photo.explanation ? <p className="mt-2 whitespace-pre-wrap text-sm text-stone-800">{photo.explanation}</p> : null}
										{photo.nextSteps ? (
											<p className="mt-2 whitespace-pre-wrap text-sm text-stone-700">
												<span className="font-semibold">Next steps: </span>
												{photo.nextSteps}
											</p>
										) : null}
									</div>
								</div>
							);
						})}
					</div>
				) : null}
				</div>
			</div>
		</div>
	);
};

export default AiReviewHistoryModal;
